import { Box, Button, ButtonProps, Group } from '@mantine/core';
import { If } from '@vision/ui/components';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './ModalFooter.module.css';
import clsx from 'clsx';

type ModalFooterProps = Partial<{
  cancelBtnLabel: string;
  cancelBtnProps: Omit<ButtonProps, 'data-testid'>;
  cancelBtnVisible: boolean;
  onCancel: VoidFunction;
  onSave: VoidFunction;
  saveBtnLabel: string;
  saveBtnProps: Omit<ButtonProps, 'data-testid'>;
  className?: string;
  position?: 'sticky' | 'fixed' | 'static';
}>;

export function ModalFooter({
  children,
  cancelBtnLabel,
  cancelBtnProps,
  cancelBtnVisible = true,
  onCancel,
  onSave,
  saveBtnLabel,
  saveBtnProps,
  className: classNameProp,
  position = 'static',
}: PropsWithChildren<ModalFooterProps>) {
  const { t } = useTranslation();
  const { className: cancelBtnClassName, ...otherCancelBtnProps } = cancelBtnProps ?? ({} as ButtonProps);

  const positionClass =
    position === 'sticky' ? classes.modalFooterSticky : position === 'fixed' ? classes.modalFooterFixed : '';

  return (
    <Box className={clsx(classes.modalFooter, positionClass, classNameProp)}>
      {children || (
        <Group justify="flex-end" gap={10}>
          <If value={cancelBtnVisible || !!(onCancel || cancelBtnProps)}>
            <Button
              className={clsx(classes.cancelBtn, cancelBtnClassName)}
              data-testid="button-modal-footer-cancel"
              onClick={onCancel}
              variant="outline"
              color="gray.5"
              miw={102}
              {...otherCancelBtnProps}
            >
              {cancelBtnLabel || t('cancel')}
            </Button>
          </If>
          <If value={!!(onSave || saveBtnProps)}>
            <Button data-testid="button-modal-footer-save" onClick={onSave} miw={84} {...saveBtnProps}>
              {saveBtnLabel || t('save')}
            </Button>
          </If>
        </Group>
      )}
    </Box>
  );
}
